import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, ShieldCheck, Loader2 } from 'lucide-react';
import useLockBodyScroll from '../utils/useLockBodyScroll';

const TPinModal = ({ isOpen, onClose, onConfirm, loading, title = "Verification Required", description = "Please enter your 4-digit Transaction PIN to proceed." }) => {
    const [pin, setPin] = useState(['', '', '', '']);
    const [error, setError] = useState('');

    // Stable refs
    const ref0 = useRef();
    const ref1 = useRef();
    const ref2 = useRef();
    const ref3 = useRef();

    const inputRefs = useMemo(() => [ref0, ref1, ref2, ref3], []);

    useLockBodyScroll(isOpen);

    useEffect(() => {
        if (isOpen) {
            setPin(['', '', '', '']);
            setError('');
            setTimeout(() => {
                if (inputRefs[0].current) inputRefs[0].current.focus();
            }, 100);
        }
    }, [isOpen, inputRefs]);

    const handleChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;

        const newPin = [...pin];
        newPin[index] = value.slice(-1);
        setPin(newPin);
        setError('');

        if (value && index < 3) {
            inputRefs[index + 1].current.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !pin[index] && index > 0) {
            inputRefs[index - 1].current.focus();
        }
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        const pinString = pin.join('');
        if (pinString.length !== 4) {
            setError('Please enter all 4 digits');
            return;
        }
        onConfirm(pinString);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Header Section */}
                <div className="bg-gradient-to-br from-indigo-600 to-blue-700 p-8 text-white relative">
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 p-2 hover:bg-white/20 rounded-full transition-colors"
                        disabled={loading}
                    >
                        <X size={20} />
                    </button>
                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-4 border border-white/20 shadow-lg">
                            <ShieldCheck className="text-white" size={32} />
                        </div>
                        <h2 className="text-2xl font-black tracking-tight">{title}</h2>
                        <p className="text-indigo-100/80 text-sm mt-2 font-medium">{description}</p>
                    </div>
                </div>

                {/* Content Section */}
                <div className="p-8 pb-10">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* PIN Inputs */}
                        <div className="flex justify-center gap-4">
                            {pin.map((digit, idx) => (
                                <input
                                    key={idx}
                                    ref={inputRefs[idx]}
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(idx, e)}
                                    disabled={loading}
                                    className={`w-14 h-16 text-center text-2xl font-black rounded-2xl border-2 transition-all outline-none
                                        ${error
                                            ? 'border-red-200 bg-red-50 text-red-600'
                                            : 'border-gray-100 bg-gray-50 focus:border-indigo-600 focus:bg-white text-indigo-700 shadow-sm'
                                        }
                                    `}
                                />
                            ))}
                        </div>

                        {error && (
                            <p className="text-center text-red-500 text-xs font-bold animate-shake">{error}</p>
                        )}

                        {/* Actions */}
                        <div className="space-y-4">
                            <button
                                type="submit"
                                disabled={loading || pin.some(d => !d)}
                                className={`w-full py-4 rounded-2xl font-black shadow-xl transition-all flex items-center justify-center gap-2 group
                                    ${loading || pin.some(d => !d)
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                                        : 'bg-indigo-600 text-white shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-1 active:scale-95'
                                    }
                                `}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} />
                                        <span>Verifying...</span>
                                    </>
                                ) : (
                                    <>
                                        {/* <Lock size={18} /> */}
                                        <span>Confirm Transaction</span>
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="w-full py-2 text-gray-400 font-bold text-xs uppercase tracking-widest hover:text-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style jsx>{`
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
                    20%, 40%, 60%, 80% { transform: translateX(4px); }
                }
                .animate-shake {
                    animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
                }
            `}</style>
        </div>
    );
};

export default TPinModal;
